import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { slugify } from '../utils';
import { BACKEND_URL } from '../config';
import ProjectForm, { ProjectFormData } from '../components/forms/ProjectForm';
import PageMeta from '../components/PageMeta';

export default function NewProject() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (data: ProjectFormData) => {
    const token = localStorage.getItem('token');
    if (!token) return navigate(-1);
    setSubmitting(true);

    if (!data.notStarted && !data.startDate) {
      toast.error(String(t('start_date_required')));
      setSubmitting(false);
      return;
    }

    const strid = slugify(data.title);
    const formData = new FormData();
    formData.append('strid', strid);
    formData.append('title', data.title);
    formData.append('titles', JSON.stringify(data.titles));
    formData.append('description', JSON.stringify(data.description));
    formData.append('subtitle', JSON.stringify(data.subtitle));
    if (data.typeId) formData.append('type_id', String(data.typeId));
    formData.append('not_started', String(data.notStarted));
    formData.append('start_date', data.startDate);
    formData.append('end_date', data.isOngoing ? 'null' : data.endDate);
    formData.append('ongoing', String(data.isOngoing));
    formData.append('team', String(data.team));
    formData.append('github_url', data.githubUrl);
    formData.append('play_store_url', data.playStoreUrl);
    formData.append('app_store_url', data.appStoreUrl);
    formData.append('website_url', data.websiteUrl);
    formData.append('version', data.version);
    formData.append('last_updated', data.lastUpdated);
    formData.append('is_public', String(data.isPublic));
    formData.append('featured', String(data.featured));
    formData.append('external_links', JSON.stringify(data.externalLinks.filter(l => l.url.trim())));
    formData.append('private_links', JSON.stringify(data.privateLinks.filter(l => l.url.trim())));
    data.selectedSkills.forEach((s, i) => formData.append(`skills[${i}]`, String(s.id)));
    data.selectedRoles.forEach((r, i) => formData.append(`roles[${i}]`, String(r.id)));
    data.images.forEach(img => formData.append('images', img));
    data.videos.forEach((v, i) => formData.append(`videos[${i}]`, v));
    if (data.logo) formData.append('logo', data.logo);
    if (data.banner) formData.append('banner', data.banner);
    if (data.showcaseBanner) formData.append('showcase_banner', data.showcaseBanner);

    try {
      const res = await fetch(`${BACKEND_URL}/api/projects`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) navigate(`/projects/${strid}`, { replace: true });
      else toast.error(String(t('error_saving')));
    } catch {
      toast.error(String(t('error_saving')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10 py-6 sm:py-10 px-4 pt-24">
      <PageMeta title={t('new_project') as string} />
      <div className="max-w-6xl mx-auto">
        <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-primary-light transition-colors mb-5 group">
          <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
          {t('projects')}
        </Link>
        <h1 className="font-display font-extrabold tracking-[-0.03em] text-4xl text-ink mb-8">{t('new_project')}</h1>
        <ProjectForm mode="new" onSubmit={handleSubmit} submitting={submitting} />
      </div>
    </div>
  );
}
